import { Loading } from '@/components/Loading';
import { Section } from '@/components/Section';
import { useLocation, useParams } from 'react-router-dom';
import {
  ExtendedTableLowCode,
  ExtendedTableLowCodeRef,
} from '@/components/shared/table/ExtendedTableLowCode';
import React, { useRef } from 'react';
import { useCustomSpec } from '@/utils/hooks/useCustomSpec';
import { Button, CustomCellProperties } from 'alurkerja-ui';
import moment from 'moment';
import { IDetailTestRunSchema } from '@/api/testRunDetail';
import { useAccess } from '@/utils/hooks/useAccess';
import { LinkButton } from '@/components/shared/button/LinkButton';
import { BiSolidShow } from 'react-icons/bi';
import { useProtectedRoute } from '@/routes/ProtectedRouteContext';
import { HideCustomCreateButton } from '@/components/shared/button/HideCustomButtonCreate';
import { ITestIssueSchema } from '@/api/testIssue';
import { UnlinkTestIssue } from '@/components/testIssue/UnlinkTestIssue';

type ListIssueTestRunProps = {
  testRun: IDetailTestRunSchema;
  toClose?: () => void;
};

interface CustomActionCellProps {
  data: ITestIssueSchema;
  testRunPage: boolean;
  detailPage: boolean;
  id: string;
  isViewer: boolean;
  toClose?: () => void;
  tableRef: React.RefObject<ExtendedTableLowCodeRef>;
}

const CustomActionCell: React.FC<CustomActionCellProps> = ({
  data,
  testRunPage,
  detailPage,
  id,
  isViewer,
  toClose,
  tableRef,
}) => {
  const isPublic = useProtectedRoute();

  let actionButton;

  if (testRunPage) {
    actionButton = (
      <a
        href={`/project/${id}/test-issue/${data.id}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Button>
          <BiSolidShow size={20} />
        </Button>
      </a>
    );
  } else if (detailPage) {
    actionButton = (
      <Button onClick={toClose}>
        <BiSolidShow size={20} />
      </Button>
    );
  } else {
    actionButton = (
      <LinkButton
        path={`/project/${id}/test-issue/${data.id}`}
        icon={<BiSolidShow size={20} />}
      />
    );
  }

  return (
    <>
      {actionButton}

      {!isViewer && !isPublic && (
        <UnlinkTestIssue
          id={data.id!}
          onSuccess={() => tableRef.current?.refetch()}
        />
      )}
    </>
  );
};

export const ListIssueTestRun: React.FC<ListIssueTestRunProps> = ({
  testRun,
  toClose,
}) => {
  const { id } = useParams();
  const ref = useRef<ExtendedTableLowCodeRef>(null);
  const isViewer = useAccess(testRun.id!);
  const location = useLocation();

  const spec = useCustomSpec(`/api/crud/test-issue`, {
    columns: [
      { key: 'code_issue', label: 'Code' },
      { key: 'title', label: 'Title' },
      { key: 'created_by', label: 'Created by' },
      { key: 'assignee_issue', label: 'Assigned to' },
      { key: 'status', label: 'Status' },
      { key: 'priority', label: 'Priority' },
      { key: 'created_at', label: 'Created Date' },
    ],
  });

  const customCell = ({
    name,
    rowValue,
    value,
    defaultCell,
  }: CustomCellProperties) => {
    if (name === 'assignee_issue') {
      return <>{rowValue.assignee ? rowValue.assignee.full_name : '-'}</>;
    } else if (name === 'created_by') {
      return <>{value ? value.full_name : '-'}</>;
    } else if (name === 'created_at') {
      return <>{moment(value).format('DD MMMM YYYY')}</>;
    }
    return defaultCell;
  };

  if (!spec) {
    return (
      <Section>
        <Loading />
      </Section>
    );
  }

  const testRunPage = location.pathname.includes('test-run');
  const testIssueId = testRun.test_run_detail_issue?.map(
    (item: any) => item.test_issue_id,
  );
  const detailPage = location.pathname.includes(`test-issue/${testIssueId}`);

  return (
    <Section title={`Issue Test Run - ${testRun.test_case!.title}`}>
      <ExtendedTableLowCode
        ref={ref}
        defaultFilter={{
          test_run_detail_id: testRun?.id,
        }}
        defaultSort="title"
        defaultSortOrder="desc"
        spec={spec}
        customButtonCreate={HideCustomCreateButton}
        customActionCell={data => (
          <CustomActionCell
            data={data as ITestIssueSchema}
            id={id!}
            testRunPage={testRunPage}
            detailPage={detailPage}
            isViewer={isViewer}
            toClose={toClose}
            tableRef={ref}
          />
        )}
        customCell={customCell}
        showFilter={false}
      />
    </Section>
  );
};
